import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, fmtDate, openBlob } from "@/lib/api";
import { DOC_TYPES } from "@/lib/constants";

const typeLabel = (v) => DOC_TYPES.find((d) => d.value === v)?.label || v;

export const DocumentsPanel = ({ quoteId, policyId, claimId, canUpload = true, compact = false, defaultType = "court_order" }) => {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState(defaultType);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const key = quoteId || policyId;

  const load = useCallback(() => {
    const params = claimId ? { claim_id: claimId } : policyId ? { policy_id: policyId } : { quote_id: quoteId };
    api.get("/documents", { params }).then((r) => setDocs(r.data)).catch(() => {});
  }, [quoteId, policyId, claimId]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File is too large (10 MB max)");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append(policyId ? "policy_id" : "quote_id", key);
    if (claimId) fd.append("claim_id", claimId);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? "" : "rounded-xl border border-slate-200 bg-white p-6"} data-testid={`documents-panel-${key}`}>
      {!compact && <h2 className="mb-1 text-lg font-bold">Documents</h2>}
      {!compact && <p className="mb-4 text-sm text-slate-500">Upload supporting documents such as court orders, DMV letters, licenses, contracts or certificates.</p>}
      {canUpload && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="w-48" data-testid={`doc-type-select-${key}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={input} type="file" accept=".pdf,image/*" className="hidden" onChange={upload} data-testid={`doc-file-input-${key}`} />
          <Button variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid={`doc-upload-btn-${key}`}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload PDF or photo
          </Button>
        </div>
      )}
      {docs.length ? (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200" data-testid={`doc-list-${key}`}>
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 p-3 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-blue-700" />
                <span className="truncate font-medium">{d.filename}</span>
                <span className="hidden text-xs text-slate-500 sm:inline">{typeLabel(d.doc_type)} · {fmtDate(d.created_at)}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => openBlob(`/documents/${d.id}/download`, d.filename).catch(() => toast.error("Could not open file"))} data-testid={`doc-view-btn-${d.id}`}>View</Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400" data-testid={`doc-empty-${key}`}>No documents yet.</p>
      )}
    </div>
  );
};
